/**
 * Named instances of a variable font file (`fvar`), found by the PostScript name a text asks for. Mirrors
 * engine/psd-render/core/src/font_instances.rs; render-rule.json referees both through pinnedOrDerivedVariation.
 *
 * A PSD keeps only the PostScript name of a layer's font — for a variable file, the name of one of its named instances
 * (`CormorantGaramond-Bold`). The file holds that instance's axis values, so they come from there, not from a word.
 */
import { splitSyntheticFontName } from '@lite-workspace/dto-fotoclass-templates';

/** Axis values by OpenType tag (`{ wght: 450, slnt: -10, opsz: 32 }`); an axis the file lacks is skipped. */
export type FontVariation = Record<string, number>;

export interface FvarAxis {
  tag: string;
  default: number;
  /** The axis range; absent (an older contract vector) — a value from a name stays unclamped. */
  min?: number;
  max?: number;
}

export interface FvarInstance {
  subfamily: string;
  /** The instance's own PostScript name, when the file gives one. */
  postScriptName?: string;
  coordinates: Record<string, number>;
}

/** The family prefixes an instance name derives from (name 25, 16, 1 in that order), the axes and the instances. */
export interface FvarInstances {
  prefixes: string[];
  axes: FvarAxis[];
  instances: FvarInstance[];
}

// Names compare by their ASCII letters and digits only, case-folded: `Inter-Bold`, `Inter Bold` and `inter_bold` are one.
const nameKey = (s: string | undefined): string => (s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The named instance whose PostScript name is `wanted`: its own name when the file gives one, else the one Adobe
 * Technical Note 5902 derives (family prefix + subfamily). `catalog` — `wanted` is a mapping row's target, the name the
 * font catalog stored for the instance (font-metadata-parser `buildInstanceMetadata`: family + subfamily, the bare
 * family for Regular). When the direct lookup misses, the target is matched in that form against every instance, an
 * instance with an own name included. A name the text asks for never takes the bare family: that is what the editor
 * writes for any weight.
 */
export function findNamedInstance(fvar: FvarInstances, wanted: string, catalog: boolean): FvarInstance | undefined {
  const want = nameKey(wanted);
  if (!want) return undefined;
  const prefixes = fvar.prefixes.map(nameKey).filter(Boolean);
  const derived = (i: FvarInstance, bareRegular: boolean): boolean => {
    const subfamily = nameKey(i.subfamily);
    return prefixes.some(p => (!!subfamily && p + subfamily === want) || (bareRegular && subfamily === 'regular' && p === want));
  };

  const direct = fvar.instances.find(i => {
    const own = nameKey(i.postScriptName);
    return own ? own === want : derived(i, catalog);
  });
  return direct ?? (catalog ? fvar.instances.find(i => derived(i, true)) : undefined);
}

/**
 * The named instance a text draws by name: its mapping row's target when the row names one (the row decides alone),
 * else the PostScript name the text asks for. `undefined` — the file names none, and the weight-label rule decides.
 * Mirrors the first step of pinned_or_derived_variation (adapter/mod.rs).
 */
export function namedInstanceOf(
  fvar: FvarInstances,
  targetPostScriptName: string | undefined,
  requestedPostScriptName: string | undefined,
): FvarInstance | undefined {
  const catalog = !!targetPostScriptName?.trim();
  const name = catalog ? targetPostScriptName : requestedPostScriptName;
  if (!name) return undefined;
  return findNamedInstance(fvar, name, catalog) ?? syntheticInstanceOf(fvar, name);
}

// `-0` reads as `0`, so both languages compare one number.
const clamp = (axis: FvarAxis, value: number): number => {
  const aboveMin = axis.min === undefined ? value : Math.max(value, axis.min);
  return (axis.max === undefined ? aboveMin : Math.min(aboveMin, axis.max)) + 0;
};

/**
 * The instance a slider-value name (`splitSyntheticFontName`) draws on this file, when its prefix is one of the file's
 * family prefixes: each named axis at its value, held inside the axis range. A tag compares with the file's axes
 * case-folded and is kept in the file's spelling; a tag the file lacks is dropped, and an axis the name leaves out stays
 * at the file's default. `undefined` — not such a name, another family's, or no axis of this file named. Mirrors
 * font_instances.rs `synthetic_instance`.
 */
export function syntheticInstanceOf(fvar: FvarInstances, wanted: string): FvarInstance | undefined {
  const name = splitSyntheticFontName(wanted);
  if (!name || !fvar.prefixes.some(p => nameKey(p) === nameKey(name.base))) return undefined;
  const coordinates: Record<string, number> = {};
  for (const [tag, value] of name.axes) {
    const axis = fvar.axes.find(a => a.tag.toLowerCase() === tag.toLowerCase());
    if (axis) coordinates[axis.tag] = clamp(axis, value);
  }

  return Object.keys(coordinates).length > 0 ? { subfamily: '', coordinates } : undefined;
}

/**
 * The key a font ref tells instances of one file apart by: a named instance's index in `fvar`, a slider-value instance's
 * axis values (it is in no list, and two values of one file must not share a ref).
 */
export function instanceRefKey(fvar: FvarInstances, instance: FvarInstance): number | string {
  const index = fvar.instances.indexOf(instance);
  if (index >= 0) return index;
  return Object.entries(instance.coordinates)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([tag, value]) => `${tag}=${value}`)
    .join(',');
}

const hasAxis = (fvar: FvarInstances, tag: string): boolean => fvar.axes.some(a => a.tag === tag);

/** True when the file slants only by `slnt` — the case italicOf resolves beyond what a weight word gives. */
export const slantsWithoutItal = (fvar: FvarInstances): boolean => hasAxis(fvar, 'slnt') && !hasAxis(fvar, 'ital');

/**
 * `variation` drawn as the italic a text asks for. A file with an `ital` axis sets it to 1. A file that slants only by
 * `slnt` takes the slant of its italic instance at the same values on every other axis, when exactly one slant is named
 * there; otherwise the variation stays as it is.
 */
export function italicOf(fvar: FvarInstances, variation: FontVariation): FontVariation {
  if (hasAxis(fvar, 'ital')) return { ...variation, ital: 1 };
  if (!hasAxis(fvar, 'slnt') || (variation['slnt'] ?? 0) !== 0) return variation;
  const valueOf = (v: Record<string, number>, axis: FvarAxis): number => v[axis.tag] ?? axis.default;
  const slants = fvar.instances
    .filter(i => (i.coordinates['slnt'] ?? 0) !== 0)
    .filter(i => fvar.axes.filter(a => a.tag !== 'slnt').every(a => valueOf(i.coordinates, a) === valueOf(variation, a)))
    .map(i => i.coordinates['slnt']);
  if (slants.length === 0 || slants.some(s => s !== slants[0])) return variation;
  const rest = Object.fromEntries(Object.entries(variation).filter(([tag]) => tag !== 'ital'));
  return { ...rest, slnt: slants[0] };
}

/**
 * `variation` drawn upright: the axes that slant (`ital`, `slnt`) at 0 when the file has them; every other axis as it
 * is. An upright run on an italic box instance (`runDropsBoxSlant`). Mirrors `FvarInstances::upright_of` (Rust).
 */
export function uprightOf(fvar: FvarInstances, variation: FontVariation): FontVariation {
  const upright = { ...variation };
  for (const tag of ['ital', 'slnt']) if (hasAxis(fvar, tag)) upright[tag] = 0;
  return upright;
}

type LocalizedName = Record<string, string> | string | undefined;

/** The slice of an opentype.js Font this reads: `names` by platform, and the parsed `fvar` table. */
export interface OpentypeFvarFont {
  names?: Record<string, Record<string, LocalizedName> | undefined>;
  tables?: {
    fvar?: {
      axes?: Array<{ tag?: string; defaultValue?: number; minValue?: number; maxValue?: number }>;
      instances?: Array<{
        name?: LocalizedName;
        postScriptName?: LocalizedName;
        subfamilyNameID?: number;
        postScriptNameID?: number;
        coordinates?: Record<string, number>;
      }>;
    };
  };
}

const englishOrFirst = (name: LocalizedName): string | undefined => {
  if (typeof name === 'string') return name || undefined;
  if (!name) return undefined;
  return name['en'] || Object.values(name).find(Boolean);
};

// The longest `name` record read, in UTF-16 units — Rust's NAME_RECORD_BYTES (512) / 2: a longer record names no instance.
const NAME_RECORD_UNITS = 256;
const readable = (text: string | undefined): text is string => !!text && text.length <= NAME_RECORD_UNITS;

const firstOf = (name: LocalizedName): string | undefined =>
  typeof name === 'string' ? (readable(name) ? name : undefined) : Object.values(name ?? {}).find(readable);

// opentype.js files a standard `name` id under its field name, any other under the id itself.
const STANDARD_NAME_KEYS: Record<number, string> = {
  1: 'fontFamily',
  2: 'fontSubfamily',
  6: 'postScriptName',
  16: 'preferredFamily',
  17: 'preferredSubfamily',
};

// A UTF-16 unit of a surrogate pair standing alone: Rust's `String::from_utf16` refuses such a record.
const UNPAIRED_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;

/**
 * A `name` id's text by the Rust rule (font_instances.rs `english_or_first_names`, the reference — print draws from
 * it): among the non-empty records of 256 UTF-16 units or fewer on the Unicode platform (any encoding) or Windows
 * encoding 0 or 1, the first with language 0x0409, else the first in table order; a chosen record that does not decode
 * names nothing, and no other platform ever names an instance.
 *
 * opentype.js hands over a dictionary, not the records, so this follows the rule only on what it keeps: it drops
 * Windows encoding 0 and reads encoding 10 as 1, keeps a Unicode record of language 0xFFFF (as `und`) but drops one of
 * any other language without an `ltag` table (with one, a language past its tags), drops Windows languages it does
 * not know, keeps the last of duplicate records, and loses the table order. The variable fonts scanned for
 * this all carry a US-English Windows record, so none is named through those inputs; full parity takes reading `name`
 * in Rust.
 */
function nameById(font: OpentypeFvarFont, id: number | undefined): string | undefined {
  if (id === undefined) return undefined;
  const key = STANDARD_NAME_KEYS[id] ?? String(id);
  const windows = font.names?.['windows']?.[key];
  const english = typeof windows === 'string' ? undefined : windows?.['en'];
  const chosen = readable(english) ? english : (firstOf(font.names?.['unicode']?.[key]) ?? firstOf(windows));

  return chosen === undefined || UNPAIRED_SURROGATE.test(chosen) ? undefined : chosen;
}

// opentype.js reads a 16.16 Fixed as `int + fraction / 65535`; the file means `fraction / 65536`, as Rust reads it.
const fixed = (value: number): number => {
  const int = Math.floor(value);
  return int + Math.round((value - int) * 65535) / 65536;
};

const fvarByFont = new WeakMap<object, FvarInstances | null>();

/** The `fvar` description of an opentype.js font, or `undefined` for a static file — read once per font object. */
export function fvarInstancesOf(font: OpentypeFvarFont | undefined): FvarInstances | undefined {
  if (!font) return undefined;
  const cached = fvarByFont.get(font);
  if (cached !== undefined) return cached ?? undefined;
  const read = readFvarInstances(font);
  fvarByFont.set(font, read ?? null);
  return read;
}

function readFvarInstances(font: OpentypeFvarFont): FvarInstances | undefined {
  const fvar = font.tables?.fvar;
  const axes = (fvar?.axes ?? [])
    .filter(a => typeof a.tag === 'string')
    .map(a => ({
      tag: a.tag as string,
      default: fixed(a.defaultValue ?? 0),
      ...(a.minValue === undefined ? {} : { min: fixed(a.minValue) }),
      ...(a.maxValue === undefined ? {} : { max: fixed(a.maxValue) }),
    }));
  if (!fvar || axes.length === 0) return undefined;
  const prefixes = [25, 16, 1].map(id => nameById(font, id)).filter((p): p is string => !!p);
  // opentype.js always sets `subfamilyNameID`, so its instances are named by id, the Rust rule. An instance's own
  // localized names stand in only for the hand-written form without ids: opentype.js resolves those from any platform
  // (Macintosh too) and without the length cap, so for an id they would name what Rust does not. A missing
  // `postScriptNameID` is opentype.js's "no own name" (no field, or 0xFFFF): it then gives `postScriptName` as `''` or
  // `undefined`, which `englishOrFirst` reads as no name, Rust's `None`.
  const instances = (fvar.instances ?? []).map(i => ({
    subfamily: (i.subfamilyNameID === undefined ? englishOrFirst(i.name) : nameById(font, i.subfamilyNameID)) ?? '',
    postScriptName: i.postScriptNameID === undefined ? englishOrFirst(i.postScriptName) : nameById(font, i.postScriptNameID),
    coordinates: Object.fromEntries(Object.entries(i.coordinates ?? {}).map(([tag, v]) => [tag, fixed(v)])),
  }));

  return { prefixes, axes, instances };
}
